import { useEffect } from 'react';
import { currencyMapper, storeKeyMapper } from '@shared/utils/src/ct';
import type { Cart } from '@storefront/lib-global/codegen/types';
import { useCartContext } from '@storefront/lib-global/context/cartContext';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import type { CreateCartMutationOptions } from '@storefront/lib-global/graphqlDocument/cart/createCart.generated';
import { useCreateCartMutation } from '@storefront/lib-global/graphqlDocument/cart/createCart.generated';
import { useNRBASession } from '@storefront/lib-global/hooks/useNRBASession';
import { setCartLocalStorageItems } from '@storefront/lib-global/utils/cartUtils';
import { getCtLocale } from '@storefront/lib-global/utils/internationalization';

export function useCreateCart() {
  const { locale } = useLocaleContext();
  const { setMiniCart, setCartId, setAnonymousId, setGuid } = useCartContext();
  const nrbaContextHeader = useNRBASession();

  const [createCartMutation, { data: createCartData, loading: isCreateCartLoading, error: createCartError }] =
    useCreateCartMutation(nrbaContextHeader);

  async function createCart(options?: CreateCartMutationOptions) {
    try {
      await createCartMutation({
        variables: {
          input: {
            currency: currencyMapper[locale],
            storeKey: storeKeyMapper(locale),
            locale: getCtLocale(locale),
          },
        },
        ...options,
      });
    } catch (e) {
      // TODO: Handle error on createCart failure.
      console.error('Error on createCart', e);
    }
  }

  useEffect(() => {
    if (createCartData) {
      const cartId = createCartData.createCart.id ?? null;
      const anonId = createCartData.createCart.anonymousId ?? null;
      const guid = createCartData.createCart.guid ?? null;
      setMiniCart(createCartData.createCart as Cart);
      setCartId(cartId);
      setAnonymousId(anonId ?? '');
      setGuid(guid ?? '');
      setCartLocalStorageItems(locale, cartId, anonId, guid);
    }
  }, [createCartData, locale, setAnonymousId, setMiniCart, setCartId, setGuid]);

  return { createCart, createCartData, isCreateCartLoading, createCartError };
}
